import { useState, useRef, useCallback, useEffect } from 'react';
import { X, Download } from 'lucide-react';
import QRCodeLib from 'qrcode';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import kahaLogo from '../../assets/Group 627737.svg';

// Intrinsic (design) size of the plate in px — matches the old lg: layout
const INTRINSIC_W = 800;
const INTRINSIC_H = 500;

const QRPlate = ({ qrPlateData, businessId, businessTag, onClose, showDownloadButtons = false }) => {
  const [plateWidth, setPlateWidth] = useState(INTRINSIC_W);
  const [plateHeight, setPlateHeight] = useState(INTRINSIC_H);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [logoSize, setLogoSize] = useState(35); // Adjustable logo size
  const plateRef = useRef(null);
  const containerRef = useRef(null);
  const [viewportFit, setViewportFit] = useState(1);

  // Generate QR code as a data URL — pure image, no canvas in DOM
  useEffect(() => {
    QRCodeLib.toDataURL(
      `https://www.kaha.com.np/?id=${businessId}&action=profile`,
      { width: 210, margin: 2, errorCorrectionLevel: 'H' }
    ).then(setQrDataUrl).catch(console.error);
  }, [businessId]);

  // Measure available viewport space and compute a fit factor
  useEffect(() => {
    const measure = () => {
      if (!containerRef.current) return;
      const parent = containerRef.current.parentElement;
      const availW = parent.clientWidth - 32;
      const availH = parent.clientHeight - containerRef.current.offsetTop - 16;
      setViewportFit(Math.min(availW / plateWidth, availH / plateHeight, 1));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [plateWidth, plateHeight]);

  const d = qrPlateData;
  const configScale = Math.min(plateWidth / INTRINSIC_W, plateHeight / INTRINSIC_H);
  const scale = configScale * viewportFit;

  const downloadPlate = useCallback(async (format = 'png') => {
    const el = plateRef.current;
    if (!el) return;

    try {
      // Capture the plate at full intrinsic size using html-to-image.
      // It serialises the DOM to SVG (foreignObject) then rasterises — works with <img>, no canvas issues.
      const dataUrl = await toPng(el, {
        width: INTRINSIC_W,
        height: INTRINSIC_H,
        pixelRatio: 2,
        style: { transform: 'none', width: `${INTRINSIC_W}px`, height: `${INTRINSIC_H}px` },
      });

      const fileName = businessTag || 'qr-plate';

      if (format === 'pdf') {
        const pdf = new jsPDF('landscape', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const margin = 10;
        const imgWidth = pageWidth - 2 * margin;
        // Load the image to get dimensions for aspect ratio
        const img = new Image();
        img.src = dataUrl;
        await new Promise((resolve) => { img.onload = resolve; });
        const imgHeight = (img.height * imgWidth) / img.width;
        pdf.addImage(dataUrl, 'PNG', margin, margin, imgWidth, imgHeight);
        pdf.save(`${fileName}.pdf`);
      } else {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `${fileName}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Download error:', err);
    }
  }, [businessTag]);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center z-50 p-4">
      {/* Controls bar */}
      <div className="flex items-center gap-4 mb-4">
        <label className="text-white text-sm flex items-center gap-2">
          W
          <input
            type="number"
            value={plateWidth}
            onChange={(e) => setPlateWidth(Math.max(200, Number(e.target.value)))}
            className="w-20 bg-white/10 border border-white/30 rounded px-2 py-1 text-white text-sm text-center"
          />
        </label>
        <span className="text-white/50">x</span>
        <label className="text-white text-sm flex items-center gap-2">
          H
          <input
            type="number"
            value={plateHeight}
            onChange={(e) => setPlateHeight(Math.max(150, Number(e.target.value)))}
            className="w-20 bg-white/10 border border-white/30 rounded px-2 py-1 text-white text-sm text-center"
          />
        </label>
        <span className="text-white/40 text-xs">px</span>

        {showDownloadButtons && (
          <>
            <button onClick={() => downloadPlate('png')} className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 text-white font-bold py-1.5 px-3 rounded-lg flex items-center gap-2 shadow-lg text-xs">
              <Download className="w-4 h-4" /> PNG
            </button>
            <button onClick={() => downloadPlate('pdf')} className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white font-bold py-1.5 px-3 rounded-lg flex items-center gap-2 shadow-lg text-xs">
              <Download className="w-4 h-4" /> PDF
            </button>
          </>
        )}

        <button onClick={onClose} className="text-gray-300 hover:text-white transition-colors ml-2">
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Scaled plate wrapper — shrinks to fit viewport */}
      <div
        ref={containerRef}
        style={{
          width: plateWidth * viewportFit,
          height: plateHeight * viewportFit,
          overflow: 'hidden',
        }}
      >
        {/* Plate at intrinsic size, scaled with CSS transform */}
        <div
          ref={plateRef}
          style={{
            width: INTRINSIC_W,
            height: INTRINSIC_H,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            boxSizing: 'border-box',
          }}
          className="bg-gradient-to-br from-[#184A6B] to-[#0F3A52] rounded-3xl border-2 border-white/30 overflow-hidden p-8"
        >
          <div className="flex flex-row gap-6 h-full">
            {/* LEFT — QR Code & Emergency Contacts */}
            <div className="flex flex-col items-center justify-center shrink-0" style={{ width: 250 }}>
              <div className="mb-6 w-full text-center">
                <div className="text-white text-lg font-bold mb-3">Scan Me</div>
                <div className="bg-white p-1 rounded-3xl inline-block relative">
                  {qrDataUrl ? (
                    <>
                      <img src={qrDataUrl} alt="QR Code" width={220} height={220} className="rounded-2xl" />
                      <div className="absolute inset-0 flex items-center justify-center rounded-2xl">
                        <img src={kahaLogo} alt="Kaha Logo" width={logoSize} height={logoSize} />
                      </div>
                    </>
                  ) : (
                    <div style={{ width: 220, height: 220 }} />
                  )}
                </div>
                <div className="text-white text-sm mt-3 font-semibold">Kaha App</div>
              </div>

              <div className="w-full text-center">
                <h3 className="text-white font-bold text-sm mb-3">आपतकालीन नम्बर:</h3>
                <div className="space-y-2 text-sm text-gray-100">
                  <div><span className="font-semibold">१००</span> प्रहरी</div>
                  <div><span className="font-semibold">१०१</span> दमकल</div>
                  <div><span className="font-semibold">१०२</span> एम्बुलेन्स</div>
                  <div><span className="font-semibold">१०४</span> बालबालिका</div>
                </div>
              </div>
            </div>

            {/* RIGHT — Business Info */}
            <div className="flex-1 min-w-0">
              <div className="border-4 border-white rounded-2xl px-8 py-6 text-center h-full flex flex-col justify-center">
                <div className="mb-4">
                  <div className="text-2xl text-gray-200 font-semibold mb-1">Kaha TAG</div>
                  <div className="text-3xl font-bold text-white break-words">@{d.tagHandle}</div>
                </div>

                <div className="bg-white rounded-xl px-4 py-3 mb-4 text-center">
                  <h2 className="text-3xl font-bold text-[#184A6B] mb-1 break-words">{d.nameEn}</h2>
                  {d.nameNp && <p className="text-xl font-semibold text-gray-600 break-words">{d.nameNp}</p>}
                </div>

                {(d.houseNumber || d.houseNumberNp) && (
                  <div className="mb-4 text-white">
                    {d.houseNumber && <div className="mb-1"><span className="font-semibold">House No : </span><span>{d.houseNumber}</span></div>}
                    {d.houseNumberNp && <div className="text-gray-200 text-sm"><span className="font-semibold">घर नम्बर : </span><span>{d.houseNumberNp}</span></div>}
                  </div>
                )}

                <div className="space-y-1 text-sm text-center">
                  {d.addressLine1En && <div className="text-gray-100 leading-relaxed break-words">{d.addressLine1En}</div>}
                  {d.addressLine1Np && <div className="text-gray-200 leading-relaxed break-words">{d.addressLine1Np}</div>}
                  {d.addressLine2En && <div className="text-gray-100 leading-relaxed break-words">{d.addressLine2En}</div>}
                  {d.addressLine2Np && <div className="text-gray-200 leading-relaxed break-words">{d.addressLine2Np}</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRPlate;
